import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  AssignablePlatform,
  AssignedUser,
  InvestmentPayment,
  useGetCompanyAssignmentsQuery,
  useUpdateCompanyAssignmentsMutation,
} from "../../store/apis/companyApi";
import { useGetUsersQuery } from "../../store/apis/usersApi";
import {
  ASSIGNABLE_PLATFORMS,
  INVESTMENT_PAYERS,
  assignableUsers,
  personLabel,
} from "../../utils/companyAssignments";

interface Props {
  companyId: string;
  canEdit: boolean;
}

type Draft = {
  partnerSuccess: AssignedUser[];
  platformOwners: Partial<Record<AssignablePlatform, AssignedUser[]>>;
  platformPayments: Partial<
    Record<AssignablePlatform, InvestmentPayment | null>
  >;
};

/**
 * Quién lleva la empresa: las personas de account / partner success y los responsables
 * de cada plataforma, varias en los dos casos. Sirve para filtrar los briefings por persona. No da acceso a la
 * empresa: eso siguen siendo los usuarios asignados.
 *
 * En cada plataforma va también quién paga por defecto (TI MEDIA GROUP o el cliente):
 * es el pago de inversión con el que nacen las líneas nuevas del briefing.
 */
const CompanyAssignmentsPanel: React.FC<Props> = ({ companyId, canEdit }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data, isFetching } = useGetCompanyAssignmentsQuery(companyId, {
    skip: !companyId,
  });
  const assignments = data?.data;

  const [draft, setDraft] = useState<Draft | null>(null);
  const editing = draft !== null;

  // La lista de usuarios solo hace falta para editar.
  const { data: usersData, isFetching: loadingUsers } = useGetUsersQuery(
    undefined,
    { skip: !editing }
  );

  const [updateAssignments, { isLoading: isSaving }] =
    useUpdateCompanyAssignmentsMutation();

  // Las personas ya asignadas se añaden a las opciones aunque hayan dejado de ser
  // asignables (desactivadas), para que se vean y se puedan quitar.
  const options = useMemo<AssignedUser[]>(() => {
    const byId = new Map<string, AssignedUser>();
    assignableUsers(usersData?.data ?? []).forEach((user) =>
      byId.set(user.id, {
        id: user.id,
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        email: user.email,
      })
    );
    assignments?.partnerSuccess.forEach((user) => {
      if (!byId.has(user.id)) byId.set(user.id, user);
    });
    assignments?.platformOwners.forEach(({ user }) => {
      if (!byId.has(user.id)) byId.set(user.id, user);
    });
    return Array.from(byId.values());
  }, [usersData, assignments]);

  const ownersOf = (platform: AssignablePlatform): AssignedUser[] =>
    (assignments?.platformOwners ?? [])
      .filter((owner) => owner.platform === platform)
      .map((owner) => owner.user);

  // `?? []`: un backend sin la migración de quién paga todavía no lo devuelve.
  const payerOf = (platform: AssignablePlatform): InvestmentPayment | null =>
    (assignments?.platformPayments ?? []).find(
      (payment) => payment.platform === platform
    )?.investmentPayment ?? null;

  const payerLabel = (payer: InvestmentPayment): string =>
    payer === "CLIENTE"
      ? t("briefingsView.paymentClient", "Cliente")
      : t("briefingsView.paymentTiMediaGroupSl", "TI MEDIA GROUP SL");

  const startEditing = () =>
    setDraft({
      partnerSuccess: assignments?.partnerSuccess ?? [],
      platformOwners: Object.fromEntries(
        ASSIGNABLE_PLATFORMS.map(({ platform }) => [
          platform,
          ownersOf(platform),
        ])
      ),
      platformPayments: Object.fromEntries(
        ASSIGNABLE_PLATFORMS.map(({ platform }) => [
          platform,
          payerOf(platform),
        ])
      ),
    });

  const save = async () => {
    if (!draft) return;
    try {
      await updateAssignments({
        companyId,
        body: {
          partnerSuccessUserIds: draft.partnerSuccess.map((user) => user.id),
          platformOwners: ASSIGNABLE_PLATFORMS.map(({ platform }) => ({
            platform,
            userIds: (draft.platformOwners[platform] ?? []).map(
              (user) => user.id
            ),
          })),
          platformPayments: ASSIGNABLE_PLATFORMS.map(({ platform }) => ({
            platform,
            investmentPayment: draft.platformPayments[platform] ?? null,
          })),
        },
      }).unwrap();
      dispatch(
        setToastMessage({
          message: t("company.assignments.saved", "Responsables guardados"),
          severity: "success",
        })
      );
      setDraft(null);
    } catch (error: unknown) {
      dispatch(
        setToastMessage({
          message:
            (error as { data?: { message?: string } })?.data?.message ??
            t("company.assignments.failed", "No se pudieron guardar"),
          severity: "error",
        })
      );
    }
  };

  const unassigned = (
    <Typography variant="body2" color="text.secondary">
      {t("company.assignments.unassigned", "Sin asignar")}
    </Typography>
  );

  const personPicker = (
    value: AssignedUser[],
    onChange: (users: AssignedUser[]) => void,
    label: string
  ) => (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={value}
      loading={loadingUsers}
      onChange={(_, users) => onChange(users)}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionLabel={(user: AssignedUser) => personLabel(user)}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={
            value.length === 0
              ? t("company.assignments.unassigned", "Sin asignar")
              : undefined
          }
          inputProps={{ ...params.inputProps, "aria-label": label }}
        />
      )}
    />
  );

  // Se etiqueta con la plataforma y "Quién paga": con solo la etiqueta del campo, las
  // diez se llamarían igual.
  const payerPicker = (
    platform: AssignablePlatform,
    value: InvestmentPayment | null,
    onChange: (payer: InvestmentPayment | null) => void
  ) => (
    <TextField
      select
      id={`payer-${platform}`}
      size="small"
      fullWidth
      value={value ?? ""}
      onChange={(event) =>
        onChange((event.target.value || null) as InvestmentPayment | null)
      }
      label={t("company.assignments.payer", "Quién paga")}
      SelectProps={{
        displayEmpty: true,
        SelectDisplayProps: {
          "aria-labelledby": `platform-${platform}-label payer-${platform}-label`,
        } as React.HTMLAttributes<HTMLDivElement>,
      }}
      InputLabelProps={{ shrink: true }}
      sx={{ mt: 1 }}
    >
      <MenuItem value="">
        <em>{t("company.assignments.payerUnset", "Sin definir")}</em>
      </MenuItem>
      {INVESTMENT_PAYERS.map((payer) => (
        <MenuItem key={payer} value={payer}>
          {payerLabel(payer)}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: "8px",
        p: 2,
        mb: 0.5,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pb: 1, mb: 1 }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Nunito",
            fontWeight: 700,
            color: theme.palette.grey[900],
          }}
        >
          {t("company.assignments.title", "Responsables")}
        </Typography>
        {canEdit && !editing && assignments && (
          <Button size="small" startIcon={<EditIcon />} onClick={startEditing}>
            {t("company.assignments.edit", "Editar responsables")}
          </Button>
        )}
      </Stack>

      {isFetching && !assignments && (
        <Box textAlign="center" py={2}>
          <CircularProgress size={22} />
        </Box>
      )}

      {assignments && (
        <Stack gap={2}>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 1 }}
              id="partner-success-label"
            >
              {t(
                "company.assignments.partnerSuccess",
                "Account / Partner success"
              )}
            </Typography>
            {editing ? (
              <Autocomplete
                multiple
                size="small"
                options={options}
                value={draft.partnerSuccess}
                loading={loadingUsers}
                onChange={(_, users) =>
                  setDraft({ ...draft, partnerSuccess: users })
                }
                isOptionEqualToValue={(option, selected) =>
                  option.id === selected.id
                }
                getOptionLabel={(user: AssignedUser) => personLabel(user)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      "aria-labelledby": "partner-success-label",
                    }}
                  />
                )}
              />
            ) : assignments.partnerSuccess.length > 0 ? (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {assignments.partnerSuccess.map((user) => (
                  <Chip key={user.id} label={personLabel(user)} />
                ))}
              </Box>
            ) : (
              unassigned
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {t(
                "company.assignments.byPlatform",
                "Responsables y quién paga por plataforma"
              )}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: editing ? "1fr 1fr" : "repeat(3, 1fr)",
                },
                gap: 1.5,
              }}
            >
              {ASSIGNABLE_PLATFORMS.map(({ platform, label }) => {
                const owners = ownersOf(platform);
                const payer = payerOf(platform);
                return (
                  <Box key={platform}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      id={`platform-${platform}-label`}
                    >
                      {label}
                    </Typography>
                    {editing ? (
                      <>
                        {personPicker(
                          draft.platformOwners[platform] ?? [],
                          (users) =>
                            setDraft({
                              ...draft,
                              platformOwners: {
                                ...draft.platformOwners,
                                [platform]: users,
                              },
                            }),
                          label
                        )}
                        {payerPicker(
                          platform,
                          draft.platformPayments[platform] ?? null,
                          (investmentPayment) =>
                            setDraft({
                              ...draft,
                              platformPayments: {
                                ...draft.platformPayments,
                                [platform]: investmentPayment,
                              },
                            })
                        )}
                      </>
                    ) : (
                      <>
                        {owners.length > 0 ? (
                          <Typography variant="body2">
                            {owners.map(personLabel).join(", ")}
                          </Typography>
                        ) : (
                          unassigned
                        )}
                        {payer && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {t("company.assignments.payer", "Quién paga")}:{" "}
                            {payerLabel(payer)}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {editing && (
            <Stack direction="row" gap={1} justifyContent="flex-end">
              <Button onClick={() => setDraft(null)} disabled={isSaving}>
                {t("common.cancel", "Cancelar")}
              </Button>
              <Button variant="contained" onClick={save} disabled={isSaving}>
                {isSaving
                  ? t("company.assignments.saving", "Guardando...")
                  : t("common.save", "Guardar")}
              </Button>
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default CompanyAssignmentsPanel;
